import { debounce } from 'lodash';

(function () {
  'use strict';

  const cssClasses = {
    controlActive: 'deakin-tab-control__link--active',
    hidden: 'hidden',
  };

  const selectors = {
    control: '.js-tab-control',
    controlItem: '.js-tab-control-item',
    controlGroup: '.js-tabs-controls',
    panel: '.js-tab-panel',
    tabGroup: '.js-tabs',
  };

  const keyupDelay = 250;

  const getControlledPanel = (control) => {
    const panelId = control.getAttribute('aria-controls');
    const panel = document.getElementById(panelId);
    return panel;
  };

  const getControls = (tabGroup) => {
    return tabGroup.querySelectorAll(selectors.control);
  };

  const getTabGroup = (el) => {
    return el.closest(selectors.tabGroup);
  };

  const initTab = (control) => {
    control.removeAttribute('tabindex');
    // Set selected state
    control.setAttribute('aria-selected', true);
    control.classList.add(cssClasses.controlActive);
    // Show controlled panel
    const panel = getControlledPanel(control);
    if (!!panel) {
      panel.classList.remove(cssClasses.hidden);
    }
  };

  const activateTab = (control) => {
    // Make tab control focusable with the TAB key
    control.focus();
    initTab(control);
  };

  const deactivateTab = (control) => {
    // Clear selected state from previously-selected control
    control.removeAttribute('aria-selected');
    control.setAttribute('tabindex', -1);
    control.classList.remove(cssClasses.controlActive);
    // Hide controlled panel
    const panel = getControlledPanel(control);
    if (!!panel) {
      panel.classList.add(cssClasses.hidden);
    }
  };

  const changeTab = (control) => {
    const tabGroup = getTabGroup(control);
    const controls = tabGroup.querySelectorAll(selectors.control);
    // Deactive all controls
    [...controls].forEach((oldControl) => {
      deactivateTab(oldControl);
    });
    // Activate specified control
    activateTab(control);
  };

  const handleClickControl = (ev) => {
    // Prevent link click
    ev.preventDefault();

    const control = ev.currentTarget;
    // Clicked control is not the currently active tab
    if (control.getAttribute('aria-selected') !== 'true') {
      changeTab(control);
    }
  };

  const handleKeyupControl = debounce((ev) => {
    const control = ev.target;
    const controlId = control.id;
    const code = ev.code;
    const tabGroup = getTabGroup(control);
    const controls = getControls(tabGroup);
    const controlsLength = [...controls].length;
    // Get the index of the currently-selected tab control
    const controlIndex = [...controls].findIndex((item) => item.id === controlId);

    const changeAdjacentTab = (newIndex) => {
      // Invalid index
      if (newIndex < 0 || newIndex >= controlsLength || newIndex === controlIndex) {
        return;
      }
      const newControl = [...controls].find((searchControl, searchControlIndex) => searchControlIndex === newIndex);
      changeTab(newControl);
    };

    const focusPanel = () => {
      const panel = getControlledPanel(control);
      panel.focus();
    };

    switch (code) {
      case 'ArrowDown':
        focusPanel();
        break;
      case 'ArrowLeft':
        changeAdjacentTab(controlIndex - 1);
        break;
      case 'ArrowRight':
        changeAdjacentTab(controlIndex + 1);
        break;
      default:
        break;
    }
  }, keyupDelay);

  const initControl = (control) => {
    control.setAttribute('role', 'tab');
    control.setAttribute('tabindex', -1);
    control.addEventListener('click', handleClickControl);
    control.addEventListener('keyup', handleKeyupControl);
  };

  const initPanel = (panel) => {
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('tabindex', -1);
  };

  const initControlGroup = (controlGroup) => {
    const items = controlGroup.querySelectorAll(selectors.controlItem);

    [...items].forEach((item, itemIndex) => {
      const control = item.querySelector(selectors.control);
      item.setAttribute('role', 'presentation');
      if (!!control) {
        initControl(control);
        /*
         * Initially activate first tab control
         * and deactivate all other controls
         */
        if (itemIndex === 0) {
          initTab(control);
        } else {
          deactivateTab(control);
        }
      }
    });
  };

  const tabGroups = document.querySelectorAll(selectors.tabGroup);
  [...tabGroups].forEach((tabGroup) => {
    const controlGroup = tabGroup.querySelector(selectors.controlGroup);
    const panels = tabGroup.querySelectorAll(selectors.panel);
    if (!!controlGroup) {
      controlGroup.setAttribute('role', 'tablist');
      initControlGroup(controlGroup);
    }
    [...panels].forEach((panel) => {
      initPanel(panel);
    });
  });
})();
